import { ROUTE_DASHBOARD, ROUTE_DASHBOARD_BOOKING_PAGE } from '@/constants/routes';
import useAuth from '@/hooks/useAuth';
import { useCreateEventTypeMutation, useListEventTypesQuery } from '@/queries/eventTypes.queries';
import { useListResourcesQuery } from '@/queries/resources.queries';
import eventTypeService from '@/services/eventType.service';
import uuidService from '@/services/uuid.service';
import EventSummary from '@/types/EventSummary';
import IResource from '@/types/IResource';
import { useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

export default function useEventList() {
	const toast = useToast();
	const auth = useAuth();
	const { t } = useTranslation();
	const queryAvailabilities = useListResourcesQuery();
	const queryListEventTypes = useListEventTypesQuery({
		enabled: queryAvailabilities.data?.resources !== undefined,
	});
	const history = useHistory();
	const queryCreateEventType = useCreateEventTypeMutation();

	function createEventType() {
		const id = uuidService.v4();
		const linkedResources = _getLinkedResources(
			queryAvailabilities.data?.resources.filter((r) => r.owner.id === auth.getCurrentUser().userId)
		);
		queryCreateEventType.mutate(
			{
				id,
				resourceRules: { linkedResources },
			},
			{
				onSuccess() {
					toast({ isClosable: true, title: t('Booking calendar created.'), status: 'success' });
					history.push(ROUTE_DASHBOARD_BOOKING_PAGE.replace(':id', id));
				},
			}
		);
	}

	function copyLink(service: EventSummary) {
		window.event?.preventDefault();
		navigator.clipboard.writeText(eventTypeService.getIdLink(service.id));
		toast({ isClosable: true, title: t('Link copied.'), status: 'success' });
	}

	return {
		status: queryListEventTypes.status,
		props: {
			action: { text: t('Create'), onClick: createEventType },
			back: { text: t('Home'), link: ROUTE_DASHBOARD },
			eventTypes: queryListEventTypes?.data?.eventTypes as Array<EventSummary>,
			history,
			copyLink,
			createEventType,
			t,
		},
	};
}

/**
 * Returns an array with the resource with tag tt_default: true
 * or an empty array otherwise
 */
function _getLinkedResources(resources: Array<IResource> | undefined): Array<{ id: string }> {
	if (!resources) {
		return [];
	}
	const defaultResource = resources.find((resource) => resource.tags?.tt_default === 'true');
	return defaultResource ? [{ id: defaultResource.id }] : [];
}
